import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp, formatDate } from "../../../utils/common";
import { useTranslation } from "react-i18next";
import useMainContentDimensions from "../../../utils/mainContentDimensions";

import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Paper as MuiPaper,
  Button,
  Typography,
  CardContent,
  Box,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import Toolbar from "../../../components/Toolbar";
import useWindowDimensions from "../../../utils/windowDimensions";
import useAuth from "../../../hooks/useAuth";
import usePaymentProcess from "../../../hooks/usePaymentProcess";
import AdvanceFilter from "./components/AdvanceFilter";
import RefreshPaymentStatus from "./components/RefreshPaymentStatus";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function DepositPaymentGatewayReport(props) {
  const { user } = useAuth();
  const [selectedRows, setSelectedRows] = useState([]);
  const { isLoadingDownload, downloadFileType, downloadTenantFile } = usePaymentProcess();
  const { width: mainContentWidth } = useMainContentDimensions();

  const { state } = useLocation();
  const { t } = useTranslation();

  const { radUserId } = props;

  const today = formatDate(new Date());

  const currentPayload = {
    filters: [
      {
        fieldName: "createdAt",
        fieldValue: `${today} 00:00:00,${today} 23:59:59`,
        op: "btn",
        dataType: "date",
        condition: "and",
        isJoin: false,
      },
    ],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [
      {
        field: "createdAt",
        sort: "DESC",
      },
    ],
  };

  const exportFile = async (fileType) => {
    await downloadTenantFile(fileType, payload);
  };

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);
  const [filterFormData, setFilterFormData] = useState({
    createdAt: [new Date(), new Date()],
    createdAtStr: `${today} 00:00:00,${today} 23:59:59`,
  });
  const { height } = useWindowDimensions();
  const { getAllDepositPaymentProcess, depositPaymentProcesses, paging, downloadFile } =
    usePaymentProcess();
  const { openDialog } = useDialog();

  const [payload, setPayload] = useState(currentPayload);

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  const fetchData = async () => {
    if (Object.keys(payload).length != 0) {
      await getAllDepositPaymentProcess(payload);
    }
  };

  const refreshPage = () => {
    setPayload(currentPayload);
    setFilterFormData({
      createdAt: [new Date(), new Date()],
      createdAtStr: `${today} 00:00:00,${today} 23:59:59`,
    });
    filterResetFunc();
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");

    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize, // Update pageSize dynamically
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Reports</Typography>
        <Typography>Deposit Payment Gateway</Typography>
      </Breadcrumbs>
    );
  };

  useEffect(() => {
    fetchData();
  }, [payload]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };
  const refreshPaymentStatus = () => {
    return openDialog(
      "refresh_payment_status",
      "Refresh Payment Status",
      <RefreshPaymentStatus
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        refreshPage={refreshPage}
      />,
    );
  };

  const columns = [
    {
      field: "orderId",
      headerName: "Order Id",
      width: 200,
    },
    {
      field: "paymentId",
      headerName: "Payment Id",
      width: 200,
    },

    {
      field: "tenantUserName",
      headerName: "User Name",
      width: 150,
    },
    {
      field: "tenantName",
      headerName: "Franchise Name",
      width: 200,
    },

    {
      field: "amount",
      headerName: "Amount",
      width: 150,
    },
    {
      field: "transDate",
      headerName: "Transaction Date",
      width: 200,
    },

    {
      field: "paymentStatus",
      headerName: "Payment Status",
      width: 150,
    },

    {
      field: "createdBy",
      headerName: "Payment Done By",
      width: 150,
    },

    {
      field: "paymentGatewayName",
      headerName: "Gateway Name",
      width: 150,
    },
  ];

  const openFilterDialog = () => {
    return openDialog(
      "advance_filter",
      "Advance Filter",
      <AdvanceFilter
        payload={payload}
        setPayload={setPayload}
        setFilterResetFunc={setFilterResetFunc}
        filterFormData={filterFormData}
        setFilterFormData={setFilterFormData}
      />,
      "md",
    );
  };

  return (
    <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64 }}>
      <CardHeader
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack on small screens
          justifyContent: "space-between",
          alignItems: "flex-start", // Adjust alignment as needed
        }}
        action={
          <Toolbar
            downloadFile={exportFile}
            refreshPage={refreshPage}
            openFilterDialog={openFilterDialog}
            isLoadingDownload={isLoadingDownload}
            downloadFileType={downloadFileType}
            refreshPaymentStatus={refreshPaymentStatus}
          />
        }
        title={
          <Typography variant="h6" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
            {t("deposit_payment_gateway_reports")}
          </Typography>
        }
        subheader={<Box sx={{ display: "flex", flexWrap: "wrap" }}>{breadCrump()}</Box>}
      />
      <CardContent sx={{ height: height - 180, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <DataGrid
            columns={columns}
            rows={depositPaymentProcesses}
            refreshPage={fetchData}
            getRowId={(row) => row["paymentTenantTransId"]}
            rowCount={paging.totalNumberOfRecords}
            loading={false}
            rowsPerPageOptions={[5]}
            pagination
            page={paging.pageNumber}
            pageSize={paging.pageSize}
            paginationMode="server"
            onPaginationModelChange={onPageChange}
            onPageSizeChange={(pageSize) => onPageChange(pageSize)}
            checkboxSelection
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
            }
            sx={{
              "& .MuiDataGrid-row.even-row": {
                backgroundColor: "#f9f9f9", // Light gray for even rows
              },
              "& .MuiDataGrid-row.odd-row": {
                backgroundColor: "#ffffff", // White for odd rows
              },
            }}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setSelectedRows(newRowSelectionModel);
            }}
            rowSelectionModel={selectedRows}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default DepositPaymentGatewayReport;
